/**
 * P&L Category Config Module
 *
 * Static TypeScript config defining the P&L hierarchy for the trading template.
 * Drives both Raw and Simplified view rendering in the financial module.
 *
 * Structure:
 *   PL_STRUCTURE — full category/subcategory/item tree
 *   getCategoryForLineItem(id) — reverse lookup: item id → category key
 *   getSimplifiedRows() — ~12 calculated total row definitions for Simplified view
 *   getAllInputRows() — all ~60 editable line items across all categories
 *
 * Sprint: SPRINT-13
 * Feature: F004 — P&L Category Config Module
 *
 * @module pl-category-config
 */

import type { PLLineItemType } from '../../types/financial';
import { PL_GROUPS } from '../../../../shared/calc/line-mapping';
import { netRevenue } from './revenue';

// ============================================================================
// Types
// ============================================================================

export type PLSign = '+' | '-' | '+/-';

export interface PLLineItemConfig {
  id: PLLineItemType;
  label: string;
  sign: PLSign;
  /** True if this value was present in the pre-Sprint-13 enum (kept for DB compatibility) */
  isLegacy?: boolean;
}

export interface PLSubcategoryConfig {
  key: string;
  label: string;
  items: PLLineItemConfig[];
}

export interface PLCategoryConfig {
  key: string;
  label: string;
  /** Input categories have editable line items */
  type: 'input';
  subcategories?: PLSubcategoryConfig[];
  /** Items directly on the category (no subcategory grouping) */
  items?: PLLineItemConfig[];
}

export interface PLCalculatedRowConfig {
  key: string;
  label: string;
  type: 'calculated';
  /** Human-readable formula for display */
  formula: string;
}

export type PLStructureEntry = PLCategoryConfig | PLCalculatedRowConfig;

// ============================================================================
// Revenue
// ============================================================================

const REVENUE_ITEMS: PLLineItemConfig[] = [
  { id: 'gross_revenue_sales', label: 'Gross Revenue / Sales', sign: '+' },
  { id: 'less_returns_allowances', label: 'Less: Returns & Allowances', sign: '-' },
  { id: 'less_discounts', label: 'Less: Discounts', sign: '-' },
  // Legacy items — kept for existing data compatibility
  { id: 'income_sales', label: 'Income / Sales', sign: '+', isLegacy: true },
  { id: 'other_income', label: 'Other Income', sign: '+', isLegacy: true },
];

// ============================================================================
// Cost of Goods Sold
// ============================================================================

const COGS_ITEMS: PLLineItemConfig[] = [
  { id: 'materials_purchases', label: 'Materials / Purchases', sign: '-' },
  { id: 'direct_labour', label: 'Direct Labour', sign: '-' },
  { id: 'manufacturing_overhead', label: 'Manufacturing Overhead', sign: '-' },
  { id: 'freight_delivery', label: 'Freight & Delivery', sign: '-' },
  { id: 'subcontractor_costs', label: 'Subcontractor Costs', sign: '-' },
  { id: 'inventory_adjustments', label: 'Inventory Adjustments', sign: '+/-' },
  // Legacy item — kept for existing data compatibility
  { id: 'cost_of_goods_sold', label: 'Cost of Goods Sold', sign: '-', isLegacy: true },
];

// ============================================================================
// Operating Expenses — Subcategories
// ============================================================================

const OPEX_PERSONNEL: PLSubcategoryConfig = {
  key: 'personnel',
  label: 'Personnel',
  items: [
    { id: 'salaries_wages', label: 'Salaries & Wages', sign: '-' },
    { id: 'bonuses_commissions', label: 'Bonuses & Commissions', sign: '-' },
    { id: 'superannuation', label: 'Superannuation', sign: '-' },
    { id: 'payroll_tax', label: 'Payroll Tax', sign: '-' },
    { id: 'workers_comp', label: 'Workers Compensation', sign: '-' },
    { id: 'training_development', label: 'Training & Development', sign: '-' },
    { id: 'recruitment', label: 'Recruitment', sign: '-' },
    { id: 'employee_benefits', label: 'Employee Benefits', sign: '-' },
    { id: 'contractors', label: 'Contractors', sign: '-' },
  ],
};

const OPEX_OCCUPANCY: PLSubcategoryConfig = {
  key: 'occupancy',
  label: 'Occupancy',
  items: [
    { id: 'rent_premises', label: 'Rent / Premises', sign: '-' },
    { id: 'utilities', label: 'Utilities', sign: '-' },
    { id: 'property_insurance', label: 'Property Insurance', sign: '-' },
    { id: 'repairs_maintenance', label: 'Repairs & Maintenance', sign: '-' },
    { id: 'cleaning', label: 'Cleaning', sign: '-' },
    { id: 'rates_levies', label: 'Rates & Levies', sign: '-' },
  ],
};

const OPEX_ADMIN_OFFICE: PLSubcategoryConfig = {
  key: 'admin_office',
  label: 'Admin & Office',
  items: [
    { id: 'office_supplies', label: 'Office Supplies', sign: '-' },
    { id: 'printing_stationery', label: 'Printing & Stationery', sign: '-' },
    { id: 'phone_internet', label: 'Phone & Internet', sign: '-' },
    { id: 'it_software', label: 'IT Software', sign: '-' },
    { id: 'it_hardware', label: 'IT Hardware', sign: '-' },
    { id: 'it_support', label: 'IT Support', sign: '-' },
    { id: 'bank_fees', label: 'Bank Fees', sign: '-' },
    { id: 'accounting_fees', label: 'Accounting Fees', sign: '-' },
    { id: 'legal_fees', label: 'Legal Fees', sign: '-' },
    { id: 'audit_fees', label: 'Audit Fees', sign: '-' },
    { id: 'consulting_fees', label: 'Consulting Fees', sign: '-' },
  ],
};

const OPEX_SALES_MARKETING: PLSubcategoryConfig = {
  key: 'sales_marketing',
  label: 'Sales & Marketing',
  items: [
    { id: 'advertising', label: 'Advertising', sign: '-' },
    { id: 'digital_seo', label: 'Digital / SEO', sign: '-' },
    { id: 'website_hosting', label: 'Website Hosting', sign: '-' },
    { id: 'sponsorships', label: 'Sponsorships', sign: '-' },
    { id: 'travel_entertainment', label: 'Travel & Entertainment', sign: '-' },
    { id: 'client_gifts', label: 'Client Gifts', sign: '-' },
    { id: 'crm_tools', label: 'CRM Tools', sign: '-' },
    { id: 'referral_fees', label: 'Referral Fees', sign: '-' },
  ],
};

const OPEX_MOTOR_VEHICLE: PLSubcategoryConfig = {
  key: 'motor_vehicle',
  label: 'Motor Vehicle',
  items: [
    { id: 'vehicle_lease_finance', label: 'Vehicle Lease / Finance', sign: '-' },
    { id: 'fuel', label: 'Fuel', sign: '-' },
    { id: 'rego_insurance', label: 'Rego & Insurance', sign: '-' },
    { id: 'vehicle_repairs', label: 'Vehicle Repairs', sign: '-' },
    { id: 'tolls_parking', label: 'Tolls & Parking', sign: '-' },
  ],
};

const OPEX_INSURANCE: PLSubcategoryConfig = {
  key: 'insurance_opex',
  label: 'Insurance',
  items: [
    { id: 'pi_insurance', label: 'PI Insurance', sign: '-' },
    { id: 'pl_insurance', label: 'PL Insurance', sign: '-' },
    { id: 'business_pack_insurance', label: 'Business Pack Insurance', sign: '-' },
    { id: 'cyber_insurance', label: 'Cyber Insurance', sign: '-' },
    { id: 'key_person_insurance', label: 'Key Person Insurance', sign: '-' },
  ],
};

const OPEX_COMPLIANCE: PLSubcategoryConfig = {
  key: 'compliance',
  label: 'Compliance',
  items: [
    { id: 'asic_regulatory', label: 'ASIC / Regulatory', sign: '-' },
    { id: 'memberships_subscriptions', label: 'Memberships & Subscriptions', sign: '-' },
    { id: 'licence_renewals', label: 'Licence Renewals', sign: '-' },
    { id: 'cpd_training', label: 'CPD Training', sign: '-' },
  ],
};

const OPEX_OTHER: PLSubcategoryConfig = {
  key: 'other_opex',
  label: 'Other',
  items: [
    { id: 'bad_debts', label: 'Bad Debts', sign: '-' },
    { id: 'donations', label: 'Donations', sign: '-' },
    { id: 'sundry_expenses', label: 'Sundry Expenses', sign: '-' },
  ],
};

/** Legacy OpEx items — kept in a virtual subcategory for display fallback */
const OPEX_LEGACY: PLSubcategoryConfig = {
  key: 'legacy_opex',
  label: 'Other (Legacy)',
  items: [
    { id: 'wages_salaries', label: 'Wages & Salaries', sign: '-', isLegacy: true },
    { id: 'insurance', label: 'Insurance', sign: '-', isLegacy: true },
    { id: 'marketing', label: 'Marketing', sign: '-', isLegacy: true },
    { id: 'professional_fees', label: 'Professional Fees', sign: '-', isLegacy: true },
    { id: 'motor_vehicle_expenses', label: 'Motor Vehicle Expenses', sign: '-', isLegacy: true },
    { id: 'office_expenses', label: 'Office Expenses', sign: '-', isLegacy: true },
    { id: 'other_operating_expenses', label: 'Other Operating Expenses', sign: '-', isLegacy: true },
  ],
};

const OPEX_SUBCATEGORIES: PLSubcategoryConfig[] = [
  OPEX_PERSONNEL,
  OPEX_OCCUPANCY,
  OPEX_ADMIN_OFFICE,
  OPEX_SALES_MARKETING,
  OPEX_MOTOR_VEHICLE,
  OPEX_INSURANCE,
  OPEX_COMPLIANCE,
  OPEX_OTHER,
  OPEX_LEGACY,
];

// ============================================================================
// D&A
// ============================================================================

const DA_ITEMS: PLLineItemConfig[] = [
  { id: 'depreciation', label: 'Depreciation', sign: '-' },
  { id: 'amortisation', label: 'Amortisation', sign: '-' },
];

// ============================================================================
// Non-Operating
// ============================================================================

const NON_OPERATING_ITEMS: PLLineItemConfig[] = [
  { id: 'interest_income', label: 'Interest Income', sign: '+' },
  { id: 'interest_expense', label: 'Interest Expense', sign: '-' },
  { id: 'gain_loss_disposal', label: 'Gain / Loss on Disposal', sign: '+/-' },
  { id: 'fx_gain_loss', label: 'FX Gain / Loss', sign: '+/-' },
  { id: 'other_non_operating_income', label: 'Other Non-Operating Income', sign: '+' },
  { id: 'other_non_operating_expense', label: 'Other Non-Operating Expense', sign: '-' },
  // Legacy item — kept for existing data compatibility
  { id: 'lease_expense', label: 'Lease Expense', sign: '-', isLegacy: true },
];

// ============================================================================
// Tax
// ============================================================================

const TAX_ITEMS: PLLineItemConfig[] = [
  { id: 'tax_expense', label: 'Tax Expense', sign: '-' },
];

// ============================================================================
// PL_STRUCTURE — Full Hierarchy
//
// Order matches the standard P&L presentation:
//   Revenue → COGS → [Gross Profit] → OpEx → [EBITDA] → D&A → [EBIT]
//   → Non-Operating → [EBT] → Tax → [NPAT]
// ============================================================================

export const PL_STRUCTURE = [
  // --- Input: Revenue ---
  {
    key: 'revenue',
    label: 'Revenue',
    type: 'input' as const,
    items: REVENUE_ITEMS,
  },
  // --- Calculated: Net Revenue ---
  {
    key: 'net_revenue',
    label: 'Net Revenue',
    type: 'calculated' as const,
    formula: 'Gross Revenue (or Income / Sales) − Returns − Discounts',
  },
  // --- Input: Cost of Goods Sold ---
  {
    key: 'cogs',
    label: 'Cost of Goods Sold',
    type: 'input' as const,
    items: COGS_ITEMS,
  },
  // --- Calculated: Gross Profit ---
  {
    key: 'gross_profit',
    label: 'Gross Profit',
    type: 'calculated' as const,
    formula: 'Net Revenue − Total COGS',
  },
  // --- Input: Operating Expenses (with subcategories) ---
  {
    key: 'opex',
    label: 'Operating Expenses',
    type: 'input' as const,
    subcategories: OPEX_SUBCATEGORIES,
  },
  // --- Calculated: Total OpEx ---
  {
    key: 'total_opex',
    label: 'Total Operating Expenses',
    type: 'calculated' as const,
    formula: 'Sum of all Operating Expenses',
  },
  // --- Calculated: EBITDA ---
  {
    key: 'ebitda',
    label: 'EBITDA',
    type: 'calculated' as const,
    formula: 'Gross Profit − Total OpEx',
  },
  // --- Input: Depreciation & Amortisation ---
  {
    key: 'da',
    label: 'Depreciation & Amortisation',
    type: 'input' as const,
    items: DA_ITEMS,
  },
  // --- Calculated: Total D&A ---
  {
    key: 'total_da',
    label: 'Total D&A',
    type: 'calculated' as const,
    formula: 'Sum of Depreciation and Amortisation',
  },
  // --- Calculated: EBIT ---
  {
    key: 'ebit',
    label: 'EBIT',
    type: 'calculated' as const,
    formula: 'EBITDA − Total D&A',
  },
  // --- Input: Non-Operating ---
  {
    key: 'non_operating',
    label: 'Non-Operating',
    type: 'input' as const,
    items: NON_OPERATING_ITEMS,
  },
  // --- Calculated: Net Non-Operating ---
  {
    key: 'net_non_operating',
    label: 'Net Non-Operating',
    type: 'calculated' as const,
    formula: 'Interest Income − Interest Expense + Gains/Losses + Other',
  },
  // --- Calculated: EBT (Profit Before Tax) ---
  {
    key: 'ebt',
    label: 'EBT / Profit Before Tax',
    type: 'calculated' as const,
    formula: 'EBIT + Net Non-Operating',
  },
  // --- Input: Tax ---
  {
    key: 'tax',
    label: 'Tax',
    type: 'input' as const,
    items: TAX_ITEMS,
  },
  // --- Calculated: NPAT ---
  {
    key: 'npat',
    label: 'NPAT',
    type: 'calculated' as const,
    formula: 'EBT − Tax Expense',
  },
] as const;

// ============================================================================
// Reverse Lookup Map
// ============================================================================

/** Maps each PLLineItemType id to the category key that contains it. */
const _categoryLookup: Partial<Record<PLLineItemType, string>> = {};

// Build the lookup map at module load time (O(n) once, O(1) per call)
for (const entry of PL_STRUCTURE) {
  if (entry.type !== 'input') continue;
  const inputEntry = entry as PLCategoryConfig;

  if (inputEntry.subcategories) {
    for (const sub of inputEntry.subcategories) {
      for (const item of sub.items) {
        _categoryLookup[item.id] = inputEntry.key;
      }
    }
  }

  if (inputEntry.items) {
    for (const item of inputEntry.items) {
      _categoryLookup[item.id] = inputEntry.key;
    }
  }
}

// ============================================================================
// Exports: Helper Functions
// ============================================================================

/**
 * Returns the top-level category key for a given line item id.
 * Returns undefined if the item is not found in any category.
 *
 * @example
 *   getCategoryForLineItem('salaries_wages')  // 'opex'
 *   getCategoryForLineItem('gross_revenue_sales')  // 'revenue'
 *   getCategoryForLineItem('depreciation')  // 'da'
 */
export function getCategoryForLineItem(lineItemId: PLLineItemType): string | undefined {
  return _categoryLookup[lineItemId];
}

/**
 * Net Revenue from standard line items: Gross Revenue − Returns − Discounts.
 * Income / Sales is the same top-line sales under its older type (the CSV template
 * and importers write it), so it stands in for Gross Revenue only when Gross
 * Revenue is empty, never on top of it (issue 2261). Other Income is excluded
 * (issue 590). Custom revenue items are added by the caller.
 */
export function sumNetRevenueItems(getValue: (id: PLLineItemType) => number): number | null {
  return netRevenue([...PL_GROUPS.revenue, ...PL_GROUPS.contraRevenue].map(lineItem => ({
    lineItem, amount: getValue(lineItem as PLLineItemType),
  })));
}

/**
 * Total COGS from standard line items (all subtractive, returned positive).
 * Custom cost_of_sales items are added by the caller.
 */
export function sumCogsItems(getValue: (id: PLLineItemType) => number): number {
  return COGS_ITEMS.reduce((total, item) => total + getValue(item.id), 0);
}

/**
 * Total OpEx from standard line items across all subcategories.
 * Custom operating_expense items are added by the caller.
 */
export function sumOpexItems(getValue: (id: PLLineItemType) => number): number {
  let total = 0;
  for (const sub of OPEX_SUBCATEGORIES) {
    for (const item of sub.items) total += getValue(item.id);
  }
  return total;
}

/**
 * Returns the calculated row definitions for the Simplified view.
 * These are the ~12 key subtotals and profit metrics displayed without
 * the full line-item detail.
 *
 * Simplified view rows (in order):
 *   Net Revenue, Total COGS, Gross Profit, Total OpEx, EBITDA,
 *   Total D&A, EBIT, Net Non-Operating, EBT, Tax, NPAT
 */
export function getSimplifiedRows(): Array<{ key: string; label: string; formula: string; isCalculated: true }> {
  const calculatedKeys = [
    'net_revenue',
    'gross_profit',
    'total_opex',
    'ebitda',
    'total_da',
    'ebit',
    'net_non_operating',
    'ebt',
    'npat',
  ];

  return PL_STRUCTURE
    .filter(entry => entry.type === 'calculated' && calculatedKeys.includes(entry.key))
    .map(entry => ({
      key: entry.key,
      label: entry.label,
      formula: (entry as { formula: string }).formula,
      isCalculated: true as const,
    }));
}

/**
 * Returns all editable (input) line items across all categories, flattened.
 * This includes both current and legacy items.
 * Used for populating spreadsheet rows in Raw view.
 *
 * @returns Array of all PLLineItemConfig objects (approx. 60 items)
 */
export function getAllInputRows(): PLLineItemConfig[] {
  const rows: PLLineItemConfig[] = [];

  for (const entry of PL_STRUCTURE) {
    if (entry.type !== 'input') continue;
    const inputEntry = entry as PLCategoryConfig;

    if (inputEntry.subcategories) {
      for (const sub of inputEntry.subcategories) {
        rows.push(...sub.items);
      }
    }

    if (inputEntry.items) {
      rows.push(...inputEntry.items);
    }
  }

  return rows;
}
